import { useCallback, useState } from 'react';
import { ApiError, NetworkError } from '@/sync/client';

interface MoneyAction<A, T> {
  run: (arg: A) => void;
  busy: boolean;
  /** Lao message to show the user. Null when there is nothing wrong. */
  error: string | null;
  dismiss: () => void;
  result: T | null;
}

/**
 * Wraps a money-committing action so a failure is visible and unambiguous.
 *
 * The server is the only place a sale exists, so a rejection here means nothing
 * happened — no sale, no payment, no balance change. That is the good case: the
 * alternative is telling a shopkeeper the sale saved when it never left the
 * phone. The message says so plainly and tells them to write it down, because
 * that is the actual next step.
 *
 * `busy` guards against a double-tap committing twice, which matters because
 * the action is a round trip rather than a synchronous return.
 *
 * The argument is passed through `run` rather than captured, so a caller can
 * commit with a value chosen in the same tap — the pay mode on the cart screen —
 * without waiting for a state update to land first.
 */
export function useMoneyAction<A, T>(
  action: (arg: A) => Promise<T>,
  onDone?: (result: T) => void,
): MoneyAction<A, T> {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<T | null>(null);

  const run = useCallback(
    (arg: A) => {
      if (busy) return;
      setBusy(true);
      setError(null);

      action(arg)
        .then((value) => {
          setResult(value);
          onDone?.(value);
        })
        .catch((cause: unknown) => {
          if (cause instanceof NetworkError) {
            setError('ຕໍ່ອິນເຕີເນັດບໍ່ໄດ້ · ລາຍການນີ້ຍັງບໍ່ຖືກບັນທຶກ ກະລຸນາຈົດໃສ່ເຈ້ຍໄວ້ກ່ອນ');
          } else if (cause instanceof ApiError) {
            // The server explained itself in Lao. Its reason is more useful
            // than anything this layer could invent.
            setError(cause.messageLo);
          } else {
            setError('ມີບັນຫາ ບໍ່ສາມາດບັນທຶກໄດ້ · ລາຍການນີ້ຍັງບໍ່ຖືກບັນທຶກ');
          }
          console.error('money action failed', cause);
        })
        .finally(() => setBusy(false));
    },
    [action, busy, onDone],
  );

  return { run, busy, error, dismiss: () => setError(null), result };
}
